import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { testimonials } from '@/mocks/testimonials';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    document.title = '关于我们 - aqdlt 官方平台';
  }, []);

  useEffect(() => {
    const existingScript = document.getElementById('about-jsonld');
    if (existingScript) existingScript.remove();

    const script = document.createElement('script');
    script.id = 'about-jsonld';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: '关于 aqdlt',
      description: '了解 aqdlt 的品牌故事、核心优势、用户口碑与联系方式。',
      url: 'https://aqdlt.com/about',
      isPartOf: {
        '@type': 'WebSite',
        name: 'aqdlt 官方平台',
        url: 'https://aqdlt.com',
      },
    });
    document.head.appendChild(script);

    return () => {
      const s = document.getElementById('about-jsonld');
      if (s) s.remove();
    };
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('idle');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    formData.delete('phone_alt');

    const urlEncoded = new URLSearchParams();
    formData.forEach((value, key) => {
      urlEncoded.append(key, String(value));
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d9snlbh4ne17ei0sdkk0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: urlEncoded.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';

      if (response.ok && parsed && parsed.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormStatus('error');
          setFormError('提交失败，请稍后重试。');
        } else {
          setFormStatus('error');
          setFormError(serverMsg || '提交失败，请稍后重试。');
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络异常，请检查网络后重试。');
    }
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        {/* Header */}
        <div className="bg-background-100">
          <Breadcrumb items={[{ label: '关于我们' }]} />
          <div className="w-full px-4 md:px-6 pb-10">
            <div className="max-w-4xl mx-auto">
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-3">
                关于 <strong className="text-primary-500">aqdlt</strong>
              </h1>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                以技术驱动创新，以服务创造价值
              </p>
            </div>
          </div>
        </div>

        {/* Brand Introduction */}
        <section className="w-full py-16 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
                品牌介绍
              </h2>
              <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                <p>
                  <strong>aqdlt</strong> 是一家专注于数字化服务与解决方案的专业平台。自成立以来，
                  aqdlt 始终秉持"技术驱动创新，服务创造价值"的核心理念，致力于为各类企业提供
                  高效、可靠、智能的 aqdlt 综合服务。
                </p>
                <p>
                  我们的团队汇聚了来自金融、科技、教育等多个领域的资深专家，拥有丰富的行业经验
                  和深厚的技术积累。通过持续的技术创新和服务优化，aqdlt 已经发展成为业内领先的
                  数字化服务平台，累计服务超过 10,000 家企业客户。
                </p>
                <p>
                  aqdlt 平台集成了智能数据分析、自动化工作流、安全云存储等核心功能模块，覆盖
                  综合服务、技术支持、商务合作、行业资讯、产品动态和成功案例六大业务领域，
                  能够为企业提供一站式的数字化转型升级解决方案。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="w-full py-16 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8">
                核心价值观
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {[
                  { icon: 'ri-focus-3-line', title: '客户至上', desc: '始终将客户需求放在首位，以卓越的服务体验赢得信任。' },
                  { icon: 'ri-lightbulb-flash-line', title: '创新驱动', desc: '持续投入技术研发，以创新引领 aqdlt 行业变革。' },
                  { icon: 'ri-team-line', title: '协作共赢', desc: '与合作伙伴携手共进，构建开放共赢的 aqdlt 生态。' },
                  { icon: 'ri-shield-star-line', title: '诚信可靠', desc: '以诚信为立业之本，用可靠的产品和服务兑现每一个承诺。' },
                ].map((val) => (
                  <div key={val.title} className="flex gap-4 p-5 rounded-lg bg-background-50">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-100 text-accent-500">
                      <i className={`${val.icon} text-lg`}></i>
                    </div>
                    <div>
                      <h3 className="text-base font-heading font-bold text-foreground-900 mb-1">{val.title}</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">{val.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Reputation / Testimonials */}
        <section className="w-full py-16 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8">
                用户口碑
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {testimonials.slice(0, 4).map((t) => (
                  <div key={t.id} className="p-5 rounded-lg bg-background-100">
                    <div className="flex items-center gap-3 mb-3">
                      <img src={t.avatar} alt={t.name} className="w-10 h-10 rounded-full object-cover" title={t.name} />
                      <div>
                        <div className="text-sm font-semibold text-foreground-900">{t.name}</div>
                        <div className="text-xs text-foreground-500">{t.company}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 mb-2">
                      {Array.from({ length: t.rating }).map((_, i) => (
                        <i key={i} className="ri-star-fill text-xs text-amber-400"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed">{t.content}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="w-full py-16 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-2xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-2 text-center">
                联系我们
              </h2>
              <p className="text-sm text-foreground-600 mb-8 text-center">
                如有任何问题或合作意向，欢迎通过以下方式联系我们
              </p>

              <form
                onSubmit={handleSubmit}
                data-readdy-form
                className="space-y-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名 <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱 <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    公司名称
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors"
                    placeholder="请输入您的公司名称（选填）"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言内容 <span className="text-red-400">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors resize-none"
                    placeholder="请输入您想咨询的内容（最多 500 字）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1">最多 500 个字符</p>
                </div>

                {/* Honeypot */}
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="absolute opacity-0 pointer-events-none"
                  style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}
                />

                <div className="pt-2">
                  <button
                    type="submit"
                    className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-3 text-sm font-medium rounded-full bg-primary-500 text-background-50 transition-all duration-300 hover:bg-primary-600 cursor-pointer whitespace-nowrap"
                  >
                    提交留言
                    <i className="ri-send-plane-line ml-2"></i>
                  </button>
                </div>

                {formStatus === 'success' && (
                  <div className="p-4 rounded-lg bg-accent-100 text-accent-800 text-sm">
                    感谢您的留言！我们会尽快与您联系。
                  </div>
                )}
                {formStatus === 'error' && (
                  <div className="p-4 rounded-lg bg-red-50 text-red-700 text-sm">
                    {formError || '提交失败，请稍后重试。'}
                  </div>
                )}
              </form>

              {/* Contact Info */}
              <div className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-6">
                {[
                  { icon: 'ri-mail-line', title: '电子邮箱', content: 'contact@aqdlt.com' },
                  { icon: 'ri-phone-line', title: '联系电话', content: '400-888-aqdlt' },
                  { icon: 'ri-map-pin-line', title: '公司地址', content: '中国 · 北京' },
                ].map((info) => (
                  <div key={info.title} className="text-center p-4 rounded-lg bg-background-50">
                    <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                      <i className={`${info.icon} text-lg`}></i>
                    </div>
                    <h3 className="text-sm font-medium text-foreground-900 mb-1">{info.title}</h3>
                    <p className="text-sm text-foreground-600">{info.content}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}